import { store, useAppStore } from '../store/appStore';
import { FILE_ACCEPT_DESKTOP, isAppleTouchDevice, loadMeshFile } from '../import/loaders';
import { exportProject3mf, shareOrDownload3mf } from '../export/project3mf';
import { capturePlateThumbnail } from '../scene/PlateViewer';
import { estimatePrint } from '../export/estimate';
import type { ToolMode } from '../store/types';
import { useEffect, useMemo, useRef, useState } from 'react';

const TOOLS: { id: ToolMode; label: string }[] = [
  { id: 'select', label: 'Select' },
  { id: 'move', label: 'Move' },
  { id: 'rotate', label: 'Rotate' },
  { id: 'scale', label: 'Scale' },
  { id: 'paint-enforce', label: 'Support+' },
  { id: 'paint-block', label: 'Block' },
  { id: 'paint-erase', label: 'Erase' },
];

export function Toolbar({ onFiles }: { onFiles?: (files: FileList | File[]) => Promise<void> }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const tool = useAppStore((s) => s.tool);
  const brushSize = useAppStore((s) => s.brushSize);
  const overhangHighlight = useAppStore((s) => s.overhangHighlight);
  const selectedId = useAppStore((s) => s.selectedId);
  const objects = useAppStore((s) => s.objects);
  const settings = useAppStore((s) => s.settings);
  const settingsOpen = useAppStore((s) => s.settingsOpen);
  const settingsPanelVisible = useAppStore((s) => s.settingsPanelVisible);
  const objectsPanelVisible = useAppStore((s) => s.objectsPanelVisible);
  const toolbarExpanded = useAppStore((s) => s.toolbarExpanded);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const ios = useMemo(() => isAppleTouchDevice(), []);
  const accept = ios ? undefined : FILE_ACCEPT_DESKTOP;

  const est = estimatePrint(objects, settings);

  useEffect(() => {
    const onToast = (e: Event) => setMsg((e as CustomEvent<string>).detail);
    window.addEventListener('p1s-toast', onToast);
    return () => window.removeEventListener('p1s-toast', onToast);
  }, []);

  async function handleFiles(files: FileList | null) {
    if (!files?.length) return;
    if (onFiles) {
      await onFiles(files);
      setMsg(`Loaded ${files.length} file${files.length > 1 ? 's' : ''}`);
      return;
    }
    for (const f of Array.from(files)) {
      try {
        const mesh = await loadMeshFile(f);
        store.addMesh(mesh.name, mesh.positions);
        setMsg(`Loaded ${mesh.name}`);
      } catch (e) {
        setMsg(`Failed: ${(e as Error).message}`);
      }
    }
  }

  async function onExport() {
    if (!objects.length) {
      setMsg('Add a model first');
      return;
    }
    setBusy(true);
    setMsg('Building project 3MF…');
    try {
      const tpl = await fetch('/bambu/project_settings.config').then((r) => r.text());
      const thumb = await capturePlateThumbnail();
      const data = exportProject3mf({
        projectSettingsText: tpl,
        settings: store.getSnapshot().settings,
        objects: store.getSnapshot().objects,
        projectName: store.getSnapshot().projectName,
        thumbnailPng: thumb,
      });
      const name = `${store.getSnapshot().projectName || 'project'}.3mf`;
      const how = await shareOrDownload3mf(data, name);
      setMsg(how === 'shared' ? 'Shared via system sheet' : `Downloaded ${name}`);
    } catch (e) {
      if ((e as Error).name !== 'AbortError') setMsg(`Export failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  function toggleSettings() {
    // Portrait: bottom sheet. Landscape: docked panel.
    const narrow = window.matchMedia('(max-width: 900px)').matches;
    if (narrow) {
      store.set({ settingsOpen: !store.getSnapshot().settingsOpen });
    } else {
      store.set({ settingsPanelVisible: !store.getSnapshot().settingsPanelVisible });
    }
    // ResizeObserver on plate will fire after layout; nudge next frame
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }

  function toggleObjects() {
    store.set({ objectsPanelVisible: !store.getSnapshot().objectsPanelVisible });
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }

  const settingsActive = settingsOpen || settingsPanelVisible;

  return (
    <div className={`toolbar ${toolbarExpanded ? 'expanded' : ''}`} data-testid="toolbar">
      <div className="brand">
        <strong>P1S Slicer</strong>
        <span className="sub">Bambu project · client-side</span>
      </div>

      <button type="button" className="primary" data-testid="upload-btn" onClick={() => fileRef.current?.click()}>
        Upload
      </button>
      <input
        ref={fileRef}
        type="file"
        // iOS Safari greys out STLs when accept lists unknown MIME types — omit on iOS
        {...(accept ? { accept } : {})}
        multiple
        hidden
        data-testid="file-input"
        onChange={(e) => {
          void handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <button
        type="button"
        className={settingsActive ? 'active' : ''}
        data-testid="toggle-settings"
        title="Show or hide settings"
        onClick={toggleSettings}
      >
        Settings
      </button>

      <button
        type="button"
        className={`desktop-only ${objectsPanelVisible ? 'active' : ''}`}
        data-testid="toggle-objects"
        title="Show or hide object list"
        onClick={toggleObjects}
      >
        Objects
      </button>

      <div className="tool-group">
        {TOOLS.slice(0, 4).map((t) => (
          <button
            key={t.id}
            type="button"
            className={tool === t.id ? 'active' : ''}
            onClick={() => store.setTool(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <button
        type="button"
        className="mobile-only"
        data-testid="toolbar-more"
        onClick={() => store.set({ toolbarExpanded: !store.getSnapshot().toolbarExpanded })}
      >
        {toolbarExpanded ? 'Less' : 'More'}
      </button>

      <div className="toolbar-extra">
        <div className="tool-group">
          {TOOLS.slice(4).map((t) => (
            <button
              key={t.id}
              type="button"
              className={tool === t.id ? 'active' : ''}
              onClick={() => store.setTool(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tool.startsWith('paint') && (
          <label className="brush">
            Brush
            <input
              type="range"
              min={1}
              max={20}
              step={0.5}
              value={brushSize}
              onChange={(e) => store.set({ brushSize: parseFloat(e.target.value) })}
            />
            <span>{brushSize}mm</span>
          </label>
        )}

        <button
          type="button"
          className={overhangHighlight ? 'active' : ''}
          onClick={() => store.set({ overhangHighlight: !overhangHighlight })}
        >
          Overhangs
        </button>

        <div className="obj-actions">
          <button type="button" disabled={!selectedId} onClick={() => store.centreSelected()}>
            Centre
          </button>
          <button type="button" disabled={!selectedId} onClick={() => store.dropSelectedToBed()}>
            Drop
          </button>
          <button type="button" disabled={!objects.length} onClick={() => store.autoArrange()}>
            Arrange
          </button>
          <button type="button" disabled={!selectedId} onClick={() => store.duplicateSelected()}>
            Dup
          </button>
          <button type="button" disabled={!selectedId} onClick={() => store.deleteSelected()}>
            Del
          </button>
          <button
            type="button"
            disabled={!selectedId}
            onClick={() => {
              const o = store.getSnapshot().objects.find((x) => x.id === selectedId);
              if (!o) return;
              store.updateObject(o.id, { rotation: [0, 0, 0] });
              store.dropSelectedToBed();
            }}
            title="Reset orientation and drop to bed"
          >
            Lay flat
          </button>
        </div>
      </div>

      <div className="estimate" title={est.note}>
        <span>~{est.filamentGrams}g</span>
        <span>~{est.approxMinutes} min</span>
        <em>estimate</em>
      </div>

      <button type="button" className="primary export" data-testid="export-btn" disabled={busy} onClick={onExport}>
        {busy ? 'Exporting…' : 'Export .3mf'}
      </button>

      {msg && <div className="toast">{msg}</div>}
    </div>
  );
}
